import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Phone, PhoneIncoming, PhoneOutgoing, Bot, Users, Clock, TrendingUp, PlayCircle,
  Sparkles, ShieldAlert, Plus, ArrowLeft, PauseCircle, Loader2, AlertCircle, CheckCircle2,
} from "lucide-react";
import { PageHead, SectionHead } from "@/components/SectionHead";
import { EmptyState } from "@/components/EmptyState";
import { KpiCard, KpiGrid } from "@/components/dashboard/KpiCard";
import { format } from "date-fns";

const LANGS = [
  { c: "en-IN", n: "English (India)" }, { c: "hi-IN", n: "Hindi" },
  { c: "gu-IN", n: "Gujarati" }, { c: "mr-IN", n: "Marathi" }, { c: "ta-IN", n: "Tamil" },
  { c: "te-IN", n: "Telugu" }, { c: "kn-IN", n: "Kannada" }, { c: "bn-IN", n: "Bengali" },
];

function CampaignEditor({ open, onOpenChange, agents, onSaved }) {
  const [f, setF] = useState({ name: "", language: "en-IN", goal: "", script: "", segment_source: "crm", segment_filter: {}, catalog_categories: [], provider_agent_id: "" });
  const [target_company_id, setTargetCompany] = useState("");
  const [companies, setCompanies] = useState([]);
  const [catInput, setCatInput] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const { data } = await api.get("/super-admin/overview").catch(()=>({data:{companies:[]}}));
      setCompanies((data.companies || []).filter(c => c.code !== "SUPER"));
    })();
  }, [open]);

  const submit = async () => {
    if (!f.name || !f.goal) return toast.error("Name and goal are required");
    setBusy(true);
    try {
      const body = { ...f, target_company_id: target_company_id || null };
      await api.post("/voice-campaigns/campaigns", body);
      toast.success("Campaign created");
      onOpenChange(false); onSaved?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">New Voice Campaign</DialogTitle>
          <DialogDescription>Only consented contacts (opt-in=true, not DND) will be called. Calling window: 09:00-21:00 IST.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label>Name</Label><Input value={f.name} onChange={(e)=>setF({...f,name:e.target.value})} placeholder="Diwali · Rose gold ring outreach" className="mt-1 h-11" data-testid="camp-name"/></div>
            <div><Label>Language</Label>
              <Select value={f.language} onValueChange={(v)=>setF({...f,language:v})}>
                <SelectTrigger className="mt-1 h-11"><SelectValue/></SelectTrigger>
                <SelectContent>{LANGS.map(l=><SelectItem key={l.c} value={l.c}>{l.n}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Agent</Label>
              <Select value={f.provider_agent_id} onValueChange={(v)=>setF({...f,provider_agent_id:v})}>
                <SelectTrigger className="mt-1 h-11"><SelectValue placeholder="Auto-pick"/></SelectTrigger>
                <SelectContent>
                  {agents.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Target company (CRM)</Label>
              <Select value={target_company_id} onValueChange={setTargetCompany}>
                <SelectTrigger className="mt-1 h-11"><SelectValue placeholder="Choose tenant"/></SelectTrigger>
                <SelectContent>{companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name} ({c.code})</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Goal / pitch</Label><Input value={f.goal} onChange={(e)=>setF({...f,goal:e.target.value})} placeholder="Introduce this month's rose-gold range" className="mt-1 h-11"/></div>
          <div><Label>Script (optional)</Label><Textarea value={f.script} onChange={(e)=>setF({...f,script:e.target.value})} placeholder="Talking points, questions to ask, offer details…" className="mt-1"/></div>
          <div className="rounded-xl border border-[#E4DFCF] p-3 bg-[#F6F1E1]/40">
            <div className="text-[10px] uppercase tracking-widest text-[#C9A24B] mb-2 font-medium">Segment filter (CRM contacts)</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <Input placeholder="State" value={f.segment_filter.state||""} onChange={(e)=>setF({...f,segment_filter:{...f.segment_filter,state:e.target.value}})} className="h-10"/>
              <Input placeholder="City" value={f.segment_filter.city||""} onChange={(e)=>setF({...f,segment_filter:{...f.segment_filter,city:e.target.value}})} className="h-10"/>
              <Input placeholder="Tag" value={f.segment_filter.tags||""} onChange={(e)=>setF({...f,segment_filter:{...f.segment_filter,tags:e.target.value}})} className="h-10"/>
            </div>
          </div>
          <div>
            <Label>Catalog categories to promote</Label>
            <div className="flex gap-2 mt-1">
              <Input value={catInput} onChange={(e)=>setCatInput(e.target.value)} placeholder="e.g. Rose Gold Rings" className="h-10 flex-1"
                onKeyDown={(e)=>{if(e.key==="Enter"){e.preventDefault(); if(catInput.trim()){setF({...f,catalog_categories:[...f.catalog_categories, catInput.trim()]}); setCatInput("");}}}}/>
            </div>
            <div className="flex flex-wrap gap-1 mt-1">
              {f.catalog_categories.map((c,i)=>(
                <span key={i} className="inline-flex items-center gap-1 rounded-full bg-[#C9A24B]/20 text-[#6E5416] text-xs px-2 py-0.5">{c}
                  <button type="button" onClick={()=>setF({...f,catalog_categories:f.catalog_categories.filter((_,j)=>j!==i)})} className="hover:opacity-70">×</button>
                </span>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={()=>onOpenChange(false)}>Cancel</Button>
          <Button className="rounded-full bg-[#0C6E3D] hover:bg-[#08331E] text-white" onClick={submit} disabled={busy} data-testid="camp-save-btn">
            {busy ? <Loader2 size={14} className="animate-spin mr-2"/> : <Plus size={14} className="mr-1"/>}Create campaign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function VoiceCampaignsPage() {
  const [config, setConfig] = useState(null);
  const [overview, setOverview] = useState(null);
  const [trends, setTrends] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [calls, setCalls] = useState([]);
  const [agents, setAgents] = useState([]);
  const [showEditor, setShowEditor] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    const [c, o, t, camps, cl, ag] = await Promise.all([
      api.get("/voice-campaigns/config").catch(()=>({data:null})),
      api.get("/voice-campaigns/analytics/overview").catch(()=>({data:null})),
      api.get("/voice-campaigns/analytics/trends?days=14").catch(()=>({data:{items:[]}})),
      api.get("/voice-campaigns/campaigns").catch(()=>({data:{items:[]}})),
      api.get("/voice-campaigns/calls?limit=25").catch(()=>({data:{items:[]}})),
      api.get("/voice-campaigns/agents").catch(()=>({data:{items:[]}})),
    ]);
    setConfig(c.data); setOverview(o.data); setTrends(t.data.items||[]);
    setCampaigns(camps.data.items||[]); setCalls(cl.data.items||[]); setAgents(ag.data.items||[]);
  };
  useEffect(() => { load(); }, []);

  const launch = async (c) => {
    if (!window.confirm(`Launch "${c.name}"? Only consented + DND-clean contacts will be called.`)) return;
    setBusy(c.id);
    try {
      const { data } = await api.post(`/voice-campaigns/campaigns/${c.id}/launch`);
      toast.success(`Launched · ${data.audience_size} contacts · mode=${data.mode}`);
      setTimeout(load, 2000);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };
  const pause = async (c) => {
    try { await api.post(`/voice-campaigns/campaigns/${c.id}/pause`); toast.success("Paused"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const kpi = overview?.kpis || {};
  const isMock = config?.mode === "mock";
  const maxCalls = Math.max(1, ...trends.map(t=>t.calls));

  return (
    <div className="space-y-6" data-testid="voice-campaigns-page">
      <PageHead
        eyebrow="TARU Platform · Automation"
        title="AI Voice Campaigns"
        subtitle="Provider: RAVAN.AI / Agni · Compliance: consent + NCPR/DND + 09-21 IST window."
        actions={
          <div className="flex gap-2 items-center">
            <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${isMock?"bg-amber-50 text-amber-800 border border-amber-200":"bg-[#E1F0E6] text-[#0F5E39] border border-[#0F5E39]/30"}`} data-testid="voice-mode-badge">
              {isMock ? <><AlertCircle size={12}/> Mock mode — set RAVAN_API_KEY to go live</> : <><CheckCircle2 size={12}/> Live · Agni</>}
            </div>
            <Button className="rounded-full bg-[#0C6E3D] hover:bg-[#08331E] text-white h-11 px-6" onClick={()=>setShowEditor(true)} data-testid="new-camp-btn">
              <Plus size={16} className="mr-1"/> New campaign
            </Button>
          </div>
        }
      />

      {isMock && (
        <Card className="border-amber-200 bg-amber-50/40 rounded-2xl"><CardContent className="p-4 text-sm text-amber-900">
          <div className="font-medium mb-1">Mock mode</div>
          Launching a campaign now generates synthetic call outcomes so the dashboard populates. To go live: add <code className="bg-white/60 rounded px-1">RAVAN_API_KEY</code> (+ optional <code className="bg-white/60 rounded px-1">RAVAN_BASE_URL</code>, <code className="bg-white/60 rounded px-1">RAVAN_WEBHOOK_SECRET</code>) to <code className="bg-white/60 rounded px-1">/app/backend/.env</code> and register <b>{`{PUBLIC_URL}/api/voice-campaigns/webhook`}</b> in Agni's dashboard.
        </CardContent></Card>
      )}

      <KpiGrid cols={6}>
        <KpiCard label="Total minutes" value={kpi.total_minutes ?? "—"} icon={Clock} tone="green"/>
        <KpiCard label="Active agents" value={kpi.active_agents ?? 0} icon={Bot} tone="green"/>
        <KpiCard label="Live campaigns" value={kpi.live_calls ?? 0} icon={PhoneOutgoing} tone="gold"/>
        <KpiCard label="Credits" value={kpi.credits ?? "—"} icon={Sparkles} tone="ivory"/>
        <KpiCard label="Success" value={(kpi.success_rate_pct||0)+"%"} icon={TrendingUp} tone="green"/>
        <KpiCard label="Conversion" value={(kpi.conversion_pct||0)+"%"} icon={CheckCircle2} tone="gold"/>
      </KpiGrid>

      {/* Trends chart (simple SVG) */}
      <Card className="border-[#E4DFCF] rounded-2xl"><CardContent className="p-5">
        <SectionHead title="Usage Trends · last 14 days" subtitle="Calls per day"/>
        <div className="w-full overflow-x-auto">
          <svg viewBox="0 0 700 160" className="w-full h-40">
            {trends.map((t,i)=>{
              const bar = (t.calls / maxCalls) * 140;
              const x = i * (700 / Math.max(trends.length, 14)) + 8;
              const w = Math.max(4, 700 / Math.max(trends.length, 14) - 12);
              return (
                <g key={t.date}>
                  <rect x={x} y={150 - bar} width={w} height={bar} rx="3" fill="#0C6E3D"/>
                  <text x={x + w/2} y={155} fontSize="9" fill="#4A5A50" textAnchor="middle">{t.date.slice(5)}</text>
                </g>
              );
            })}
            {trends.length === 0 && <text x="350" y="80" fontSize="14" fill="#4A5A50" textAnchor="middle">No data yet — launch a campaign.</text>}
          </svg>
        </div>
      </CardContent></Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Outcomes */}
        <Card className="border-[#E4DFCF] rounded-2xl"><CardContent className="p-5">
          <SectionHead title="Call outcomes" subtitle="Lifetime"/>
          <div className="space-y-2 text-sm">
            {[
              ["Total", overview?.outcomes?.total || 0, "bg-[#F1EDDF] text-[#4A5A50]"],
              ["Connected", overview?.outcomes?.connected || 0, "bg-[#E1F0E6] text-[#0F5E39]"],
              ["Voicemail", overview?.outcomes?.voicemail || 0, "bg-[#F6EED8] text-[#6E5416]"],
              ["Declined", overview?.outcomes?.declined || 0, "bg-red-50 text-red-700"],
            ].map(([l,v,cls])=>(
              <div key={l} className="flex items-center justify-between">
                <span>{l}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${cls}`}>{v}</span>
              </div>
            ))}
          </div>
        </CardContent></Card>

        {/* Agents */}
        <Card className="border-[#E4DFCF] rounded-2xl"><CardContent className="p-5">
          <SectionHead title="Available agents" subtitle={`Provider: Agni · ${agents.length} agents`}/>
          <div className="space-y-2 text-sm">
            {agents.length === 0 && <div className="text-[#4A5A50]">No agents found — create one in Agni dashboard.</div>}
            {agents.map(a => (
              <div key={a.id} className="flex items-center justify-between border-b border-[#E4DFCF] pb-2">
                <div>
                  <div className="font-medium">{a.name}</div>
                  <div className="text-xs text-[#4A5A50]">{a.language} · {a.id?.slice(0,10)}</div>
                </div>
                <Bot size={16} className="text-[#0C6E3D]"/>
              </div>
            ))}
          </div>
        </CardContent></Card>

        {/* Today activity */}
        <Card className="border-[#E4DFCF] rounded-2xl"><CardContent className="p-5">
          <SectionHead title="Today's activity" subtitle="Since 00:00 IST"/>
          <div className="grid grid-cols-2 gap-3 text-center">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Total</div>
              <div className="font-serif text-3xl text-[#0C6E3D]">{overview?.today?.total || 0}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Connected</div>
              <div className="font-serif text-3xl text-[#C9A24B]">{overview?.today?.connected || 0}</div>
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-[#4A5A50] mt-3 flex items-center gap-1">
            <ShieldAlert size={11}/> Window: {config?.within_calling_window_ist ? "OPEN" : "CLOSED (outside 09-21 IST)"}
          </div>
        </CardContent></Card>
      </div>

      {/* Campaigns */}
      <SectionHead title={`Campaigns (${campaigns.length})`}/>
      {campaigns.length === 0 ? (
        <EmptyState icon={PhoneOutgoing} eyebrow="No campaigns yet"
          title="Create your first outbound campaign"
          description="Segment your consented CRM, pick a language + Agni agent, launch. Every call writes back a rating, preferences, transcript and triggers a WhatsApp catalog nudge."
          action={{ label: "New campaign", onClick: ()=>setShowEditor(true), testId: "empty-camp-btn" }}/>
      ) : (
        <Card className="border-[#E4DFCF] rounded-2xl overflow-hidden"><CardContent className="p-0">
          <table className="w-full text-sm">
            <thead><tr className="bg-[#F6F1E1]/60 text-[10px] uppercase tracking-widest text-[#4A5A50]">
              <th className="p-3 text-left">Campaign</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3 text-left">Made</th>
              <th className="p-3 text-left">Connected</th>
              <th className="p-3 text-left">VM · Declined</th>
              <th className="p-3 text-right">Actions</th>
            </tr></thead>
            <tbody>{campaigns.map((c)=>(
              <tr key={c.id} className="border-t border-[#E4DFCF]">
                <td className="p-3">
                  <div className="font-medium">{c.name}</div>
                  <div className="text-xs text-[#4A5A50]">{c.language} · {c.goal}</div>
                </td>
                <td className="p-3">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${c.status==="running"?"bg-[#E1F0E6] text-[#0F5E39]":c.status==="completed"?"bg-[#F6EED8] text-[#6E5416]":c.status==="paused"?"bg-amber-50 text-amber-700":"bg-[#F1EDDF] text-[#4A5A50]"}`}>{c.status}</span>
                </td>
                <td className="p-3 font-mono">{c.calls_made}</td>
                <td className="p-3 font-mono text-[#0F5E39]">{c.calls_connected}</td>
                <td className="p-3 font-mono text-xs text-[#4A5A50]">{c.voicemail} · {c.declined}</td>
                <td className="p-3 text-right">
                  <div className="inline-flex gap-1">
                    {c.status !== "running" && c.status !== "completed" && (
                      <Button size="sm" className="rounded-full bg-[#0C6E3D] hover:bg-[#08331E] text-white h-9" onClick={()=>launch(c)} disabled={busy===c.id} data-testid={`launch-${c.id}`}>
                        {busy === c.id ? <Loader2 size={12} className="animate-spin mr-1"/> : <PlayCircle size={12} className="mr-1"/>} Launch
                      </Button>
                    )}
                    {c.status === "running" && (
                      <Button size="sm" variant="outline" className="rounded-full h-9" onClick={()=>pause(c)}><PauseCircle size={12} className="mr-1"/>Pause</Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}</tbody>
          </table>
        </CardContent></Card>
      )}

      {/* Recent calls */}
      <SectionHead title="Recent calls" subtitle="Last 25"/>
      {calls.length === 0 ? (
        <div className="text-sm text-[#4A5A50] py-6 text-center rounded-2xl border border-dashed border-[#E4DFCF]">No calls yet. Launch a campaign to see live results.</div>
      ) : (
        <Card className="border-[#E4DFCF] rounded-2xl overflow-hidden"><CardContent className="p-0">
          <table className="w-full text-sm">
            <thead><tr className="bg-[#F6F1E1]/60 text-[10px] uppercase tracking-widest text-[#4A5A50]">
              <th className="p-3 text-left">When</th>
              <th className="p-3 text-left">Contact</th>
              <th className="p-3 text-left">Outcome</th>
              <th className="p-3 text-left">Rating</th>
              <th className="p-3 text-left">Duration</th>
              <th className="p-3 text-left">WhatsApp</th>
            </tr></thead>
            <tbody>{calls.map(c => (
              <tr key={c.id} className="border-t border-[#E4DFCF]">
                <td className="p-3 text-xs">{format(new Date(c.created_at), "d MMM HH:mm")}</td>
                <td className="p-3">
                  <div>{c.contact_name || "—"}</div>
                  <div className="text-xs text-[#4A5A50] font-mono">{c.phone || ""}</div>
                </td>
                <td className="p-3">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${c.outcome==="connected"?"bg-[#E1F0E6] text-[#0F5E39]":c.outcome==="voicemail"?"bg-[#F6EED8] text-[#6E5416]":"bg-red-50 text-red-700"}`}>{c.outcome}</span>
                </td>
                <td className="p-3 text-xs">{c.lead_rating || "—"}</td>
                <td className="p-3 text-xs">{c.duration_seconds ? `${c.duration_seconds}s` : "—"}</td>
                <td className="p-3 text-xs">{c.whatsapp_sent ? "✓ queued" : "—"}</td>
              </tr>
            ))}</tbody>
          </table>
        </CardContent></Card>
      )}

      <CampaignEditor open={showEditor} onOpenChange={setShowEditor} agents={agents} onSaved={load}/>
    </div>
  );
}
